import React, { Component } from 'react';
import classnames from 'classnames';

import { InputLabel, MenuItem, Select } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import { WithStyles, withStyles } from '@mui/styles';
import APMultilevelDropdownStyle from "./styles/APMultilevelDropdown.style";

import { ReactComponent as ChevronDown } from "./assets/icon-chevron-down.svg";
import APSubDropdown from "./APSubDropdown";
import FindNestedArrayValue from "../../../core/utils/FindNestedArrayValue";
import { GroupAffiliateOptions } from "../../../core/types/GeneralTypes";
import _ from "lodash";

interface APMultilevelDropdownProps extends WithStyles<typeof APMultilevelDropdownStyle> {
  data: GroupAffiliateOptions[],
  label: string,
  onChange?: Function,
  name: string,
  value?: any
}

class APSelectMultiLevelDropdown extends Component<APMultilevelDropdownProps, {}> {
  state = {
    selectedParentOptionValue: '',
    dropDownData: [],
    selectedOption: null,
    open: false,
    isMobileView: false
  };

  constructor(props: APMultilevelDropdownProps) {
    super(props);
    this.onChange = this.onChange.bind(this);
    this.toggleSelectOpen = this.toggleSelectOpen.bind(this);
    this.onMainOptionSelected = this.onMainOptionSelected.bind(this);
    this.subOptionSelected = this.subOptionSelected.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  handleResize() {
    const { isMobileView } = this.state;
    const showMobileView: boolean = window.innerWidth <= 480;
    if (isMobileView !== showMobileView) {
      this.setState({ isMobileView: showMobileView });
    }
  }

  toggleSelectOpen = () => {
    this.setState({ open: !this.state.open });
  };

  onChange(event: React.SyntheticEvent<any, Event> | Event | any) {
    const {
      name,
      onChange
    } = this.props;
    const { value = '' } = event.target || {};
    const { dropDownData } = this.state;
    const { selectedObject = '' } = FindNestedArrayValue(dropDownData, value)
    const selectedOption: GroupAffiliateOptions | string = selectedObject ?? '';

    if (onChange && selectedOption !== '') {
      onChange({
        target: {
          name,
          value: selectedOption['value']
        }
      });
    }
    this.setState({ open: false });
  }

  onMainOptionSelected(selectedOptionValue: string, parentSelectedValue: string) {
    if (parentSelectedValue !== '') {
      const event = {
        target: {
          value: selectedOptionValue
        }
      }
      this.onChange(event as React.ChangeEvent<any>)
    }
  }

  subOptionSelected = (selectedSubOption: GroupAffiliateOptions) => {
    const {
      name,
      onChange
    } = this.props;

    if (selectedSubOption) {
      const { value } = selectedSubOption;
      if (onChange) {
        onChange({
          target: {
            name,
            value: value ?? ''
          }
        });
      }
    }
    this.setState({ open: false });
  };

  componentDidMount(): void {
    const {
      data,
      value
    } = this.props;

    if (data && data.length > 0) {
      this.setState({ dropDownData: data });
      if (value !== '') {
        const { selectedObject, parentObjectValue } = FindNestedArrayValue(data, value);
        this.setState({
          selectedOption: selectedObject,
          ...(parentObjectValue !== '' && { selectedParentOptionValue: parentObjectValue })
        })
      }
      this.handleResize();
    }

    let resizeTimer: ReturnType<typeof setTimeout>;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.handleResize();
      }, 500);
    });
  }

  componentDidUpdate(prevProps: Readonly<APMultilevelDropdownProps>, prevState: Readonly<any>) {
    const {
      data,
      value: selectedValue
    } = this.props;
    const {
      selectedParentOptionValue
    } = this.state;

    const isDropdownDataChanged: boolean = data.length > 0 && !(_.isEqual(prevProps.data, data));
    if (isDropdownDataChanged) {
      this.setState({ dropdownData: data })
    }

    const isSelectedValueChanged: boolean = !(_.isEqual(prevProps.value, selectedValue));
    const isSelectedParentValueChanged: boolean = !(_.isEqual(prevState.selectedParentOptionValue, selectedParentOptionValue));

    if ((isSelectedValueChanged && selectedValue !== '') || isSelectedParentValueChanged) {
      const { selectedObject, parentObjectValue } = FindNestedArrayValue(data, selectedValue);
      this.setState({
        selectedOption: selectedObject,
        selectedParentOptionValue: parentObjectValue
      })
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  render() {
    const {
      classes,
      label,
      name,
      value: selectedValue
    } = this.props;

    const { dropDownData, selectedOption, selectedParentOptionValue, open, isMobileView } = this.state;
    const inputValue = selectedParentOptionValue !== '' ? selectedParentOptionValue : selectedValue;

    return (
      <FormControl className={classes.formControlCls} variant="outlined">
        <InputLabel htmlFor="outlined-select">{label}</InputLabel>
        <Select
          IconComponent={ChevronDown}
          value={inputValue}
          onChange={this.onChange}
          renderValue={() => selectedOption !== null ? selectedOption!['label'] : ''}
          label={label}
          open={open}
          onOpen={this.toggleSelectOpen}
          onClose={this.toggleSelectOpen}
          inputProps={{
            name: `${name}`,
            id: 'outlined-select',
            "data-testid": "select-input"
          }}
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left'
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            classes: { paper: classes.selectMenuPopover },
            variant: 'menu'
          }}
        >
          {dropDownData.map((option: GroupAffiliateOptions, index: number) => {
            const { value, label: optionLabel, subAffiliate } = option;
            return (
              <MenuItem
                key={index}
                data-testid="select-option"
                value={value}
                className={classnames(classes.menuItem, {
                  [classes.subMenuItem]: subAffiliate?.length > 0,
                  [classes.responsiveMenuItem]: isMobileView
                })}>
                {subAffiliate?.length > 0 ?
                  <APSubDropdown
                    parentMenuData={option}
                    selectedAffiliateValue={selectedValue}
                    subOptionSelected={this.subOptionSelected}
                    selectParentOption={(selectedParentValue: string) => this.onMainOptionSelected(selectedParentValue, selectedParentOptionValue)}
                    isParentValueSelected={selectedParentOptionValue !== ''}
                    isMobileView={isMobileView}
                  />
                  :
                  optionLabel
                }</MenuItem>)
          })}
        </Select>
      </FormControl>
    );
  }
}

export default withStyles(APMultilevelDropdownStyle)(APSelectMultiLevelDropdown);
